import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SignInInfo, SignInSignOutDocument } from '@app/models/user.model';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { UserService } from '@app/services/user/user.service';
import { LocalizationService } from '../internationalization/localization.service';
import { VisitorTypeUploadReason } from '@app/models/visitortypeuploadreason.model';
import { SiteService } from '@app/services/site/site.service';
import { SiteVisitReason } from '@app/models/sitevisitreason.model';
import { ReferenceService } from '@app/services/reference/reference.service';
import { CameraService } from '@app/services/camera/camera.service';
import { CameraResultType, CameraSource } from '@capacitor/camera';
import { UtilsService } from '@app/services/util/util-service';
import { AuthService } from '@app/services/auth/auth.service';
import { FileStorageService } from '@app/services/filestorage/filestorage.service';
import { from } from 'rxjs';
import { environment } from '@env/environment';
import { UploadFileStorage } from '@app/models/filestorage.model';

@Component({
  selector: 'app-site-induction-completed-add-photo',
  templateUrl: './site-induction-completed-add-photo.component.html',
  styleUrl: './site-induction-completed-add-photo.component.scss',
  standalone: false
})
export class SiteInductionCompletedAddPhotoComponent implements OnInit {
   public addPhotoFormGroup!: UntypedFormGroup;
   signInSignOutInfo : SignInInfo | undefined;
   visitorTypeUploadReasons : VisitorTypeUploadReason[] = [];
   siteVisitReasons: SiteVisitReason[] = [];
   fromList: boolean = false;
   localeId : string | undefined;

   constructor(
        public _router: Router,
        private _navigationService: NavigationService,
        private _formBuilder: UntypedFormBuilder,
        private _userService: UserService,
        private _localizationService: LocalizationService,
        private _siteService: SiteService,
        private _referenceService: ReferenceService,
        private _cameraService: CameraService,
        private _utilsService: UtilsService,
        private _authService: AuthService,
        private _fileStorageService: FileStorageService,
        private _activatedRoute: ActivatedRoute
      ) {
          this._navigationService.onChange(false, false);
          this.fromList = this._activatedRoute.snapshot.paramMap.get('fromList') === 'true';
          this.addPhotoFormGroup = this.initAddPhotoForm();
        }
      
      
  closeAddPhoto() {
    if (this.fromList) {
      this._router.navigate(['photolist']);
    }
    else {
      this._router.navigate(['siteinductioncompleted']);
    }
  }

  private initAddPhotoForm(): UntypedFormGroup {
    const addPhotoFormGroup = this._formBuilder.group({
       uploadReasonSelection: new UntypedFormControl(undefined, [Validators.required]),
       comments: new UntypedFormControl('', [Validators.maxLength(500)]),
    });
    return addPhotoFormGroup;
  }

  ngOnInit(): void {
    this.localeId = environment.defaultLang.toLowerCase();;
    this._localizationService.get().then((l) => {
      if (l) {
        this.localeId = l.toLowerCase();
      }
    });
    
    this._userService.getSignIn().then(signInInfo => {
      if (signInInfo !== undefined && signInInfo.signInSignOutId !== undefined && signInInfo.signInSignOutId !== "") {
        this.signInSignOutInfo = signInInfo;
        this.getUploadReasons();
      }
      else {
        alert(this._localizationService.translate('induction_completed_add_photo_no_signin_found'));
        this._router.navigate(['signin']);
      }
    });
  }

  async getUploadReasons() {
    this.siteVisitReasons = await this._siteService.getVisitReasons();
    let visitorTypeId = "00000000-0000-0000-0000-000000000000";
    if (this.siteVisitReasons && this.siteVisitReasons.length > 0 &&
        this.siteVisitReasons[0].visitorTypeId && this.siteVisitReasons[0].visitorTypeId != "") {
      visitorTypeId = this.siteVisitReasons[0].visitorTypeId;
    }
    this._referenceService.getUploadReasons(visitorTypeId, true).subscribe({
      next: async (uploadReasons) => {
        if (uploadReasons && uploadReasons.length > 0) {
          this.visitorTypeUploadReasons = uploadReasons;
          this.visitorTypeUploadReasons.forEach(ur => {
            ur.displayDescription = ur.translations!.find(s => s.languageCode.toLowerCase() ==  this.localeId)?.description ?? ur.description;
            if (ur.isRequired) {
              ur.displayDescription = ur.displayDescription + ' ' + this._localizationService.translate('induction_completed_add_photo_required');
            }
          });
        }
      },
      error: error => {
        if (error.error && error.error.errorCode) {
          alert(this._localizationService.translate(error.error.errorCode));
        }
      }
    })
  }

  async capturePhoto() {
    try {
      const photo = await this._cameraService.takePhoto({ resultType: CameraResultType.DataUrl, source: CameraSource.Camera }); 
      if (!photo || !photo.dataUrl) {
        alert(this._localizationService.translate('induction_completed_add_photo_camera_no_photo_captured'));
        return;
      }
      else {
         let imageFormatFromBase64String = this._utilsService.getBase64StringFileExtension(photo.dataUrl);
         let imagePath = photo.dataUrl;
         let comments = this.addPhotoFormGroup.get('comments')?.value;
         if (comments && comments.length > 500) {
           alert(this._localizationService.translate('induction_completed_add_photo_comments_exceed_maxlength').replace('{max_length}', '500'));
           return;
         }
         this._authService.getFileSettings()
            .then(fs => {
              let formats = fs.supportedImages?.replace(/^,+|,+$/g, '').split(",").join("|");
              if (formats && formats.length > 0) {
                let matches = imageFormatFromBase64String.match(new RegExp(`(${formats})$\w?`));
                if (matches == null) {
                  alert(this._localizationService.translate("induction_completed_add_photo_invalid_filetype").replace('{supported_formats}', fs.supportedImages!.toLocaleUpperCase()));
                } else {
                  var newFile = {
                      fileType: matches[0] ? matches[0] : "",
                      fileName: `${crypto.randomUUID()}.${matches[0]}`
                  } as UploadFileStorage;
                  var newImagePath = imageFormatFromBase64String === "jpg" ? this._utilsService.removeExifMetadataFromDataUrl(imagePath) : imagePath;
                  newFile.file = newImagePath.split(",")[1];

                  const fileBytes = Math.floor(this.base64ByteLength(newFile.file));
          
                  if (fileBytes > ((fs?.maxDocumentSizeInMbs ?? environment.maxFileSize) * 1024 * 1024)) {
                    alert(this._localizationService.translate("induction_completed_add_photo_exceedmaxfilesize")
                          .replace('{file_size}', (fs?.maxDocumentSizeInMbs ?? environment.maxFileSize).toString()));
                  } else {

                    this._fileStorageService.UploadImage(newFile).subscribe({
                      next: r => {
                        if (r && r.id != undefined) {
                          this.saveSignInSignOutDocument(r.id);
                        }
                      },
                      error: e => {
                        if (e.error && e.error.errorCode) {
                          alert(this._localizationService.translate(e.error.errorCode));
                        }
                        else {
                          alert(this._localizationService.translate("induction_completed_add_photo_error_uploading_photo"));
                        }
                      }
                    });

                  }
                }
              } else alert(this._localizationService.translate("induction_completed_add_photo_supported_fileformats_not_found"));
            });
      }
   
    } catch (error: any) {
      const msg =
        error?.message?.includes('NotAllowedError') ? this._localizationService.translate('induction_completed_add_photo_camera_permission_denied') :
        error?.message?.includes('NotFoundError') ? this._localizationService.translate('induction_completed_add_photo_camera_no_camera_detected') :
        error?.message ?? this._localizationService.translate('induction_completed_add_photo_camera_unexpected_error');
      alert(msg);
    }

  }

  base64ByteLength(b64: string): number {
    const padding = (b64.match(/=+$/) || [''])[0].length;
    return (b64.length * 3) / 4 - padding;
  }

  async saveSignInSignOutDocument(fileStorageId: string) {
    let uploadReasonId = this.addPhotoFormGroup.get('uploadReasonSelection')?.value;
    let comments = this.addPhotoFormGroup.get('comments')?.value;

    if (this.signInSignOutInfo && uploadReasonId) {
      var signInSignOutDocument = {
        signInSignOutId: this.signInSignOutInfo.signInSignOutId,
        fileStorageId: fileStorageId,
        visitorTypeUploadReasonId: uploadReasonId,
        comments: comments
      } as SignInSignOutDocument;

      this._userService.createSignInSignOutDocument(signInSignOutDocument).subscribe({
        next: async r => {
          if (r) {
            alert(this._localizationService.translate("induction_completed_add_photo_created_successfully"));
            if (this.fromList) {
              this._router.navigate(['photolist']);
            }
            else {
              this._router.navigate(['siteinductioncompleted']);
            }
          } else {
            alert(this._localizationService.translate("induction_completed_add_photo_created_failed"));
            await this._fileStorageService.RemoveDocument(fileStorageId);
          }
        },
        error: async error => {
          if (error.error && error.error.errorCode) {
            alert(this._localizationService.translate(error.error.errorCode));
          }
          else {
            alert(this._localizationService.translate('induction_completed_add_photo_created_failed'));
          }
          await this._fileStorageService.RemoveDocument(fileStorageId);
        }
      });
    }
    else {
      alert(this._localizationService.translate('induction_completed_add_photo_no_signin_uploadreason_found'));
      await this._fileStorageService.RemoveDocument(fileStorageId);
    }
   
  } 

  public hasError(form: UntypedFormGroup, controlName: string): boolean {
    const validationOutput = this.getError(form, controlName);
    return validationOutput !== '';
  }

  public getError(form: UntypedFormGroup, controlName: string): string {
    switch (controlName) {
      case 'comments':
        if (this.formHasError(form, controlName, 'maxlength')) {
          return this._localizationService.translate('induction_completed_add_photo_comments_exceed_maxlength').replace('{max_length}', '500');
        } 
        break;
    }
    return '';
  }

  private formHasError(form: UntypedFormGroup, controlName: string, errorName: string): boolean {
    return form.controls[controlName].hasError(errorName);
  }

    public disableCameraButton(): boolean {
    return !this.addPhotoFormGroup.valid;
  }
}
